import { useEffect } from 'react';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormSelectInput } from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import {
  assignPromoCodeToUtilityUser,
  SalesActions,
  useAccountDispatch,
  useAccountSelector,
} from '@wajeez-web/stores/account';
import { BiLoaderAlt } from 'react-icons/bi';
import {
  useAssignPromoCodeToUtilityUser,
  useGetPromoCodeAliasesLookups,
} from '@wajeez-web/components/helpers';
import { getPromoCodeAliasesLookups } from '@wajeez-web/stores/product';

type Input = {
  promoCodeAliasId: number;
};

interface AssignPromoCodeToSaleUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => Promise<void> | void;
  saleUserId?: number | undefined;
}

export const AssignPromoCodeToSaleUserModal = ({
  isOpen,
  onClose,
  onSubmit,
  saleUserId,
}: AssignPromoCodeToSaleUserModalProps) => {
  const accountDispatch = useAccountDispatch();

  const assignPromoCodeToSaleUserState = useAssignPromoCodeToUtilityUser();

  const promoCodeAliasesLookups = useGetPromoCodeAliasesLookups();

  const saleUserById = useAccountSelector(({ sales }) => sales.utilityUserById);

  const { handleSubmit, control, reset } = useForm<Input>();

  const onFormSubmit: SubmitHandler<Input> = async (data) => {
    assignPromoCodeToUtilityUser(
      {
        saleUserId: saleUserId,
        ...data,
      },
      onSubmit
    );
  };

  useEffect(() => {
    if (saleUserById.data) {
      reset({
        promoCodeAliasId: Number(saleUserById.data.promoCodeAliasId),
      });
    }
  }, [saleUserById.data?.promoCodeAliasId]);

  useEffect(() => {
    if (saleUserId) {
      accountDispatch(SalesActions.getUtilityUserByIdAsync(Number(saleUserId)));
    }

    getPromoCodeAliasesLookups();
  }, [saleUserId, accountDispatch]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Sale User"
      alertTarget="modal"
    >
      <div className="w-full">
        {saleUserById.status === 'loading' ||
          promoCodeAliasesLookups.status === 'loading' ? (
          <div className="w-full min-h-[200px] flex justify-center items-center">
            <Spin />
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            noValidate
            className="w-full flex flex-col justify-start items-start"
          >
            <div className="w-[300px] flex flex-col justify-start items-start space-y-2">
              <FormSelectInput
                name="promoCodeAliasId"
                control={control}
                fieldType={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {
                    selectOptions:
                      promoCodeAliasesLookups.data?.map((p) => {
                        return {
                          value: p.id || '',
                          label: p.code || '',
                        };
                      }) || [],
                  },
                  required: true,
                  label: 'Promo Code Alias',
                }}
                errorMessages={{ required: 'This field is required' }}
                widthClassName="w-full"
                showErrors
              />
            </div>
            <div className="mt-6 w-full flex justify-end items-center space-s-2">
              <div className="w-[70px]">
                <ButtonHeadless
                  bgStyle="bg-warmGray-300"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                  action={onClose}
                >
                  <div className="text-warmGray-600 text-[16px] font-medium">
                    <ReturnTranslatedText
                      text="buttons.Cancel"
                      origintext="Cancel"
                    />
                  </div>
                </ButtonHeadless>
              </div>
              <div className="w-[70px]">
                <ButtonHeadless
                  type="submit"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                >
                  <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                    <ReturnTranslatedText
                      text="buttons.Save"
                      origintext="Save"
                    />
                    {assignPromoCodeToSaleUserState.status === 'loading' && (
                      <BiLoaderAlt className="animate-spin w-5" />
                    )}
                  </div>
                </ButtonHeadless>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};
